import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateDraftInvoiceSupervisingPartnerNotificationMutation } from 'store/api/tenants.ts';
import { DraftInvoiceSupervisingPartnerNotificationUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

const DraftInvoiceSupervisingPartnerNotificationControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateDraftInvoiceSupervisingPartnerNotification] =
        useUpdateDraftInvoiceSupervisingPartnerNotificationMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);

    const { control } = useForm<DraftInvoiceSupervisingPartnerNotificationUpdateDto>({
        defaultValues: {
            isDraftInvoiceNotifiedToSupervisingPartner: tenantConfiguration.isDraftInvoiceNotifiedToSupervisingPartner,
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.draft-invoice-notified-to-supervising-partner'),
                id: 'isDraftInvoiceNotifiedToSupervisingPartner',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: () => (
                    <CheckboxInput
                        control={control}
                        name="isDraftInvoiceNotifiedToSupervisingPartner"
                        disabled={!hasUpdatePermission}
                        onChange={(checked) =>
                            updateDraftInvoiceSupervisingPartnerNotification({
                                isDraftInvoiceNotifiedToSupervisingPartner: checked,
                            })
                        }
                    />
                ),
            },
        ],
        [control, hasUpdatePermission, updateDraftInvoiceSupervisingPartnerNotification]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.draft-invoice-notification-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-draft-invoice-supervising-partner-notification"
            />
        </>
    );
};

export default DraftInvoiceSupervisingPartnerNotificationControl;
